import A from 'components/general/A';
import LabeledCheckbox from 'components/general/inputs/LabeledCheckbox';
import LabeledInput from 'components/general/inputs/LabeledInput';
import StyledButton from 'components/general/StyledButton';
import StyledLabel from 'components/general/StyledLabel';
import { IErrorResponse, ILocationState, ISelectOption } from 'components/general/types';
import { IUser } from 'components/general/types/general';
import WaveContainer from 'components/general/WaveContainer';
import AuthWidget from 'components/general/widgets/AuthWidget';
import { setUserId } from 'components/RootView/missionExplorerSlice';
import useStyles from 'components/SignUpView/styles';
import { useUserForm } from 'components/SignUpView/useUserForm';
import { EMAIL_REGEX, gaEvents, IS_CES, LICENSE_AGREEMENT_URL, TERMS_OF_USE_URL } from 'config';
import { useFormikForm, useSnackbar, useUser } from 'hooks';
import { SatelliteApi } from 'middleware/SatelliteApi/api';
import { useEffect, useState } from 'react';
import ReactGA from 'react-ga4';
import { useDispatch } from 'react-redux';
import { useHistory, useLocation, withRouter } from 'react-router-dom';
import Routes from 'routes';
import { COUNTRIES } from 'utils/forms';
import * as Yup from 'yup';

interface IForm {
  firstName: string;
  lastName: string;
  organization: string;
  userCountryCode: ISelectOption | '';
  organizationCountryCode: ISelectOption | '';
  email: string;
  licenseAgreementAccepted: boolean;
}

const defaultValues: IForm = {
  firstName: '',
  lastName: '',
  organization: '',
  userCountryCode: '',
  organizationCountryCode: '',
  email: '',
  licenseAgreementAccepted: false,
};

const requiredMessage = 'All fields are required';
const licenseMessage = 'You must agree to the Sedaro License Agreement to continue.';
const maxMessage = (name: string, length: number) => {
  return `${name} must be no more than ${length} characters`;
};

/**
 * Custom form for SSO users to verify/fill in their information before accessing Sedaro.
 *
 * SSO users also use this form to update their information at any time, in which case
 * it's only accessible via a redirect from the SSO provider to verify their account before edits are made.
 */
const SsoSignup = () => {
  const { classes } = useStyles();
  const history = useHistory();
  const location = useLocation<ILocationState>();
  const dispatch = useDispatch();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const user = useUser();

  const signUpSchema = Yup.object().shape({
    firstName: Yup.string().required(requiredMessage).max(128, maxMessage('First name', 128)),
    lastName: Yup.string().required(requiredMessage).max(128, maxMessage('Last name', 128)),
    organization: Yup.string().required(requiredMessage).max(128, maxMessage('Organization', 128)),
    userCountryCode: Yup.object().required(requiredMessage),
    organizationCountryCode: Yup.object().required(requiredMessage),
    email: Yup.string()
      .required('Your email is required.')
      .max(320, maxMessage('Email', 320))
      .matches(EMAIL_REGEX, 'Please enter a valid email address.'),
    licenseAgreementAccepted: Yup.boolean().when('patch', {
      is: (val: string) => !isPatch,
      then: Yup.boolean().required(licenseMessage).oneOf([true], licenseMessage),
    }),
  });

  // This route is used both for new users and to patch old ones
  const isPatch = user.isVerified;

  const { from } = location.state || {
    from: { pathname: isPatch ? Routes.ACCOUNT() : Routes.ROOT() },
  };

  const {
    User: {
      actions: { completeProfile, logout, setProfileIncomplete, updateUser },
    },
  } = SatelliteApi;

  useEffect(() => {
    if (!user.incomplete) {
      history.replace(Routes.ROOT());
    }
  }, [user, history]);

  const onSubmit = (values: IForm) => {
    setLoading(true);
    const func = isPatch ? updateUser : completeProfile;

    dispatch(
      func({
        email: values.email,
        firstName: values.firstName,
        lastName: values.lastName,
        organization: values.organization,
        userCountryCode: values.userCountryCode,
        organizationCountryCode: values.organizationCountryCode,
        successCallback: (user: IUser) => {
          dispatch(setUserId(user.id));
          setLoading(false);
          if (!isPatch) {
            ReactGA.event(gaEvents.SIGNUP, {
              category: 'Account',
              action: 'Sign Up',
              label: 'SSO Sign Up Link',
            });
          } else {
            enqueueSnackbar(
              'Profile successfully updated.' +
                ' If you changed your contact email, please verify your new email by clicking the link we just sent to your updated address. Until then, your old email will stay active.',
              { variant: 'success', persist: true }
            );
          }
          // Callback to redirect user to their intended route (otherwise root) once successfully authenticated
          history.replace(from);
        },
        failureCallback: (response: IErrorResponse) => {
          // failure callback, backend validation
          if (response.error.code === 'UNIQUENESS_VIOLATION') {
            enqueueSnackbar(
              'An account with that email address already exists. Please contact us if you think this is a mistake.'
            );
          } else {
            enqueueSnackbar(response.error.message);
          }
          setLoading(false);
        },
      })
    );
  };

  const { formik } = useFormikForm<IUser, IForm>(defaultValues, onSubmit, signUpSchema, user, {
    options: {
      userCountryCode: COUNTRIES,
      organizationCountryCode: COUNTRIES,
    },
  });
  const { getFieldProps, handleSubmit } = formik;
  const userForm = useUserForm<IForm>(formik);

  return (
    <WaveContainer>
      <AuthWidget>
        <form onSubmit={handleSubmit}>
          <h2 className={classes.ssoHeader}>
            Complete your profile{!isPatch && ' to access Sedaro'}
          </h2>
          {userForm}
          <LabeledInput
            {...getFieldProps('email')}
            label="Email (We'll communicate with you here)"
            type="text"
            placeholder="Email"
            autoComplete="on"
          />
          {!isPatch && (
            <div className={classes.disclaimer}>
              <LabeledCheckbox {...getFieldProps('licenseAgreementAccepted')} />
              <StyledLabel>
                I acknowledge and agree to Sedaro's{' '}
                <A href={TERMS_OF_USE_URL} target="_blank">
                  Terms of Service (ToS)
                </A>
                {!IS_CES && (
                  <>
                    {' '}
                    and{' '}
                    <A href={LICENSE_AGREEMENT_URL} target="_blank">
                      SaaS License Agreement
                    </A>
                  </>
                )}
                , including export control related restrictions and obligations.
              </StyledLabel>
            </div>
          )}
          <StyledButton type="submit" disabled={loading} loading={loading} tallMargin fullWidth>
            {isPatch ? 'Submit' : 'Complete Sign Up'}
          </StyledButton>
          <StyledButton
            // This button logs you out if you're still creating their profile,
            // and just takes you back to the account page if you're patching
            type="button"
            onClick={() => {
              closeSnackbar();
              if (!isPatch) {
                dispatch(
                  logout({
                    successCallback: (params: { redirect?: string }) => {
                      dispatch(setUserId(undefined));
                      if (params.redirect) {
                        window.location.href = params.redirect;
                      }
                    },
                  })
                );
              } else {
                dispatch(
                  setProfileIncomplete({
                    id: user.id,
                    incomplete: false,
                  })
                );
                history.push(Routes.ACCOUNT());
              }
            }}
            framed
            fullWidth
          >
            {isPatch ? 'Back' : 'Logout'}
          </StyledButton>
        </form>
      </AuthWidget>
    </WaveContainer>
  );
};

export default withRouter(SsoSignup);
